import get from 'lodash/get';
import moment from 'moment';
import { chartUtils, palette } from '@hc/chart-dot-jsx';
import * as d3 from 'd3';

import { MARKET_INDEX_LABELS } from 'legacy/appstore/constants';

import {
  abbrDollarsFormatter,
  dollarsFormatter,
  numberFormatter,
  abbrNumberFormatter
} from 'legacy/utils/formatters';

import { CHART_DATA_VALUE_FNS } from 'legacy/services/chart-data';

import MarketIndexChartTooltipContent from 'legacy/components/MarketIndexChartTooltipContent';

import subjectLogo from 'legacy/assets/images/icons/subject.png';

import styleVars from '../styles/common/variables.css-module.css';

export const buildChartConfigForecast = (data, areaType, avmKey) => {
  const FORECAST_YEAR_RADIUS = 8;
  const {
    gradientLinearForecast3,
    gradientLinearForecast2,
    gradientLinearForecast1,
    gradientLinearForecastPast
  } = palette;
  const COLORS = [
    'id:gradientLinearForecastPast',
    'id:gradientLinearForecast1',
    'id:gradientLinearForecast2',
    'id:gradientLinearForecast3'
  ];
  return {
    chart: {
      id: 'forecastChart',
      width: '100%',
      height: 400,
      margin: {
        top: 15,
        bottom: 40,
        right: 15,
        left: 75
      }
    },
    tooltip: {
      shared: {
        enabled: true,
        formatX: (x) => moment(x).format('MMM YYYY'),
        formatY: dollarsFormatter,
        line: {
          color: styleVars.colorTextLight
        },
        zIndex: 4
      },
      singular: {
        enabled: true,
        formatY: (y) => d3.format('$,')(Math.round(y))
      }
    },
    defs: {
      gradient: [
        gradientLinearForecast3,
        gradientLinearForecast2,
        gradientLinearForecast1,
        gradientLinearForecastPast
      ]
    },
    series: [
      ...get(data, 'byYear', []).map((dataset, y) => {
        return {
          id: `forecastChartSeries-${y}`,
          dataId: `${avmKey}--${data.dataId}--${areaType}--${y}`,
          type: 'area',
          data: data.byYear[y],
          fill: COLORS[y],
          tooltip: {
            enabled: false
          },
          stroke: {
            width: 0
          },
          zIndex: 1
        };
      }),
      {
        id: `forecastChartSeries-line`,
        dataId: `${avmKey}--${data.dataId}--${areaType}--line`,
        type: 'area',
        data: data.full,
        zIndex: 3,
        tooltip: {
          type: 'shared'
        },
        stroke: {
          width: 3,
          color: palette.lineOrange
        },
        fill: 'transparent'
      },
      {
        id: `forecastChartSeries-scatter`,
        dataId: `${avmKey}--${data.dataId}--${areaType}--scatter`,
        type: 'scatter',
        data: data.forecast,
        zIndex: 5,
        fill: '#FFF',
        stroke: {
          width: 3,
          color: palette.lineOrange
        },
        radius: FORECAST_YEAR_RADIUS,
        tooltip: {
          adjustY: -20,
          adjustX: 2.5,
          type: 'singular'
        },
        onMouseOver: function (d, e) {
          d3.select(e.target).transition().duration(500).attr('r', 12);
          d3.select(`#forecast-${d.month}-pct`)
            .transition()
            .duration(250)
            .ease(d3.easeLinear)
            .style('transform', 'scale(1.2)');
        },
        onMouseOut: function (d, e) {
          d3.select(e.target)
            .transition()
            .duration(500)
            .attr('r', FORECAST_YEAR_RADIUS);
          d3.select(`#forecast-${d.month}-pct`)
            .transition()
            .duration(250)
            .ease(d3.easeLinear)
            .style('transform', 'scale(1)');
        }
      },
      {
        id: `forecastChartSeries-current`,
        dataId: `${avmKey}--${data.dataId}--${areaType}--current`,
        type: 'scatter',
        data: data.current,
        zIndex: 5,
        fill: styleVars.colorBlue,
        stroke: {
          width: 3,
          color: '#FFF'
        },
        radius: 16,
        pointImg: subjectLogo,
        pointWidth: 55,
        pointHeight: 55,
        shape: 'image',
        adjustX: -28,
        adjustY: -28,
        tooltip: {
          type: 'singular',
          adjustY: -25,
          adjustX: 2.5
        }
      }
    ],
    yAxis: {
      scale: {
        type: 'linear',
        floor: 'min'
      },
      ticks: {
        color: 'transparent',
        width: 1,
        label: {
          format: (y) => abbrDollarsFormatter(y)
        }
      },
      line: {
        color: 'transparent'
      },
      gridLines: {
        color: 'rgba(0, 0, 0, 0.05)'
      },
      zIndex: 2
    },
    xAxis: {
      scale: {
        type: 'time'
      },
      zIndex: 2,
      adjustY: 5,
      ticks: {
        height: 1,
        width: 1,
        label: {
          adjustY: 15,
          format: (x) => moment(x).format('YYYY')
        },
        offsetHeight: 3
      },
      line: {
        color: 'transparent',
        width: 0
      }
    }
  };
};

export const buildChartConfigNearbyProperties = (
  data,
  chartType,
  chartDataKeySelected,
  subjectValue
) => {
  const valueFormatters = {
    dolSqFt: (v) => dollarsFormatter(v),
    sqFt: (v) => abbrNumberFormatter(v),
    bedrooms: (v) => Math.round(v),
    bathrooms: (v) => Math.round(v),
    siteArea: (v) => abbrNumberFormatter(v),
    dolSiteArea: (v) => dollarsFormatter(v),
    age: (v) => Math.floor(v)
  };

  const _isSubjectBucket = (d) =>
    subjectValue >= d.x[0] && subjectValue <= d.x[1];

  const { gradientLinearOrange, gradientLinearGrey } = palette;

  const dataWithSubject = [];
  if (data && data[chartDataKeySelected] && data[chartDataKeySelected].length) {
    data[chartDataKeySelected].forEach((d) => {
      // Add subject value to the appropriate bucket (no need to save multiple sets of chart data anymore)
      if (_isSubjectBucket(d)) {
        dataWithSubject.push({
          x: d.x,
          y: d.y + 1
        });
      } else {
        dataWithSubject.push(d);
      }
    });
  }

  return {
    chart: {
      height: '100%',
      width: '100%',
      margin: {
        top: 60,
        right: 15,
        bottom: 90,
        left: 50
      }
    },
    defs: {
      gradient: [gradientLinearOrange, gradientLinearGrey]
    },
    xAxis: {
      label: {
        text: CHART_DATA_VALUE_FNS[chartDataKeySelected].chartTypeLabel,
        fontWeight: 'bold',
        adjustY: function () {
          return this.adjustedXaXisTickLabelHeight || 50;
        }
      },
      ticks: {
        width: 1,
        color: styleVars.colorBorder,
        length: 20,
        position: 'between',
        label: {
          adjustY: 10,
          format: (x) => {
            const range = x;
            if (range[1] - range[0] <= 1) {
              return valueFormatters[chartDataKeySelected](range[0]);
            } else {
              return `${valueFormatters[chartDataKeySelected](
                range[0]
              )} - ${valueFormatters[chartDataKeySelected](range[1])}`;
            }
          }
        }
      },
      scale: {
        type: 'band',
        // Must be between 0-1, is percentage relative to bar width: https://github.com/d3/d3-scale#band_padding
        padding: 0.15
      }
    },
    yAxis: {
      line: {
        color: 'transparent'
      },
      label: {
        text: 'Properties',
        color: styleVars.colorText,
        fontWeight: 'bold'
      },
      ticks: {
        count: 5,
        color: 'transparent',
        label: {
          fontSize: 12,
          format: (y) => numberFormatter(y)
        }
      },
      gridLines: {
        color: styleVars.colorBorder
      },
      scale: {
        type: 'linear'
      }
    },
    series: {
      id: `nearby-chart`,
      dataId: `nearby-chart--${chartType}--${chartDataKeySelected}--${subjectValue}`,
      type: 'bar',
      data: dataWithSubject,
      animation: {
        delay: (d, i) => i * 50,
        ease: d3.easePolyIn
      },
      fill: (d) =>
        _isSubjectBucket(d)
          ? 'id:gradientLinearOrange'
          : 'id:gradientLinearGrey',
      label: {
        format: (d) => numberFormatter(d.y),
        adjustY: -20,
        fontSize: 12,
        fontWeight: (d) => (_isSubjectBucket(d) ? 'bold' : 'normal')
      },
      stroke: {
        width: 2,
        color: (d) => (_isSubjectBucket(d) ? '#FEB913' : '#E5E5E5')
      }
    }
  };
};

export const buildChartConfigMarketIndex = (data, addressId) => {
  const MARKET_INDEX_COLORS = {
    sSeller: '#61a801',
    seller: '#b9bf02',
    neutral: '#a3a3a3',
    buyer: '#ff8600',
    sBuyer: '#cf3d24'
  };

  const _buildMarketIndexChartSeries = () => {
    let series = [];
    for (let l in data) {
      series.push({
        id: l,
        dataId: `${addressId}--${l}--data`,
        type: 'line',
        legend: {
          label: MARKET_INDEX_LABELS[l],
          color: MARKET_INDEX_COLORS[l]
        },
        stroke: {
          color: MARKET_INDEX_COLORS[l],
          width: 5
        },
        tooltip: {
          type: 'shared',
          formatY: (y) => y.toFixed(1)
        },
        data: data[l]
      });
    }
    return series;
  };

  return {
    chart: {
      height: 350,
      width: '100%',
      margin: {
        top: 10,
        bottom: 40,
        left: 50,
        right: 15
      },
      id: `market-index-chart--${addressId}`
    },
    tooltip: {
      shared: {
        enabled: true,
        format: (points) => (
          <MarketIndexChartTooltipContent
            points={points}
            colors={MARKET_INDEX_COLORS}
          />
        ),
        line: {
          width: 1
        }
      }
    },
    legend: {
      title: 'Market Type',
      type: 'vertical',
      enabled: true
    },
    series: _buildMarketIndexChartSeries(data),
    yAxis: {
      scale: {
        floor: 0,
        ceil: 100
      },
      label: {
        text: 'Market index'
      }
    },
    xAxis: {
      ticks: {
        label: {
          adjustY: 10,
          format: d3.timeFormat('%Y')
        },
        values: chartUtils.getXValuesByYear
      }
    }
  };
};

export const buildChartConfigDaysOnMarket = (data, addressId) => {
  return {
    chart: {
      height: 250,
      width: '100%',
      margin: {
        top: 10,
        bottom: 40,
        left: 50,
        right: 15
      },
      id: `days-on-market--${addressId}`
    },
    tooltip: {
      shared: {
        enabled: true,
        formatX: (x) => moment(x).format("MMM D, 'YY"),
        formatY: (y) => `${Math.round(y)} days`,
        line: {
          width: 1
        }
      }
    },
    series: {
      id: 'days-on-market--series',
      dataId: 'days-on-market--data',
      type: 'line',
      stroke: {
        color: styleVars.colorOrange,
        width: 5
      },
      tooltip: {
        type: 'shared'
      },
      data
    },
    yAxis: {
      scale: {
        floor: 'min',
        ceil: 'max',
        paddingFloor: 5,
        paddingCeil: 5
      },
      label: {
        text: 'Days on Market'
      },
      ticks: {
        count: 3
      }
    },
    xAxis: {
      ticks: {
        label: {
          adjustY: 10,
          format: d3.timeFormat('%Y')
        },
        values: chartUtils.getXValuesByYear
      }
    }
  };
};

export const buildChartConfigMonthsOfSupply = (data, addressId) => {
  return {
    chart: {
      height: 250,
      width: '100%',
      margin: {
        top: 10,
        bottom: 40,
        left: 50,
        right: 15
      },
      id: `months-of-supply--${addressId}`
    },
    tooltip: {
      shared: {
        enabled: true,
        formatX: (x) => moment(x).format("MMM D, 'YY"),
        formatY: (y) => y.toFixed(1),
        line: {
          width: 1
        }
      }
    },
    series: {
      id: 'months-of-supply--series',
      dataId: 'months-of-supply--data',
      type: 'line',
      stroke: {
        color: styleVars.colorOrange,
        width: 5
      },
      tooltip: {
        type: 'shared'
      },
      data
    },
    yAxis: {
      scale: {
        floor: 'min',
        ceil: 'max',
        paddingFloor: 0.1,
        paddingCeil: 0.1
      },
      label: {
        text: 'Months of Supply'
      },
      ticks: {
        count: 3
      }
    },
    xAxis: {
      ticks: {
        label: {
          adjustY: 10,
          format: d3.timeFormat('%Y')
        },
        values: chartUtils.getXValuesByYear
      }
    }
  };
};
